import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { ArrowLeft, CalendarCheck2, ChevronRight, ClipboardList, Copy, PencilLine, UserRound } from 'lucide-react';
import { toast } from 'sonner';
import { FormAppointmentInstructorsPanel } from '@/components/forms/FormAppointmentInstructorsPanel';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { RescheduleFeeWaiversPanel } from '@/components/forms/RescheduleFeeWaiversPanel';
import { usePermissions } from '@/hooks/usePermissions';
import { dynamicFormsApi } from '@/lib/api/dynamic-forms';
import type { DynamicForm, DynamicFormStatus } from '@/types/dynamic-form';
import type { FormField } from '@/types/form-definition';
import { can } from '@/utils/permissions';
import { cn } from '@/lib/utils';

function statusBadgeClass(status: DynamicFormStatus) {
  switch (status) {
    case 'published':
      return 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400';
    case 'archived':
      return 'border-muted-foreground/25 bg-muted text-muted-foreground';
    default:
      return 'border-amber-500/30 bg-amber-500/10 text-amber-800 dark:text-amber-400';
  }
}

export default function FormDetailPage() {
  const { formId } = useParams<{ formId: string }>();
  const { permissions, adminType, loading: permLoading } = usePermissions();
  const [form, setForm] = useState<DynamicForm | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!formId) return;
    setError(null);
    setLoading(true);
    try {
      const res = await dynamicFormsApi.get(formId);
      if (res.statusCode !== 200) {
        throw new Error(res.message || 'Failed to load form');
      }
      setForm(res.data.form);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load form');
      setForm(null);
    } finally {
      setLoading(false);
    }
  }, [formId]);

  useEffect(() => {
    void load();
  }, [load]);

  const appointmentFieldOptions = useMemo(() => {
    const fields = (form?.draftDefinition?.fields ?? []) as FormField[];
    return fields
      .filter((f) => f.type === 'appointment')
      .map((f) => ({
        id: f.id,
        label: (typeof f.label === 'string' && f.label.trim() ? f.label.trim() : f.id) as string,
      }));
  }, [form?.draftDefinition?.fields]);

  const draftAppointmentFields = useMemo(
    () => (form?.draftDefinition?.fields ?? []).filter((f) => f.type === 'appointment') as FormField[],
    [form?.draftDefinition?.fields]
  );

  const canViewInstructorAssignments = can(permissions, 'forms.appointments.instructors.view', adminType);

  const copyInstructorAttendanceUrl = async () => {
    if (!form?._id || typeof window === 'undefined') return;
    const path = `/instructor/forms/${form._id}/attendance`;
    const url = `${window.location.origin}${path}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Instructor attendance link copied');
    } catch {
      toast.error('Could not copy to clipboard');
    }
  };

  return (
    <div className="mx-auto max-w-6xl space-y-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <Button variant="ghost" size="sm" className="w-fit gap-2 px-0 text-muted-foreground" asChild>
          <Link to="/f">
            <ArrowLeft className="h-4 w-4" />
            All forms
          </Link>
        </Button>
        {form && (
          <Button size="sm" className="gap-2 shadow-sm" asChild>
            <Link to={`/f/${form._id}/edit`}>
              <PencilLine className="h-4 w-4" />
              Edit form
            </Link>
          </Button>
        )}
      </div>

      {loading ? (
        <div className="space-y-4">
          <Skeleton className="h-10 w-2/3" />
          <Skeleton className="h-24 w-full rounded-xl" />
        </div>
      ) : error ? (
        <Card className="border-destructive/30 bg-destructive/5">
          <CardHeader>
            <CardTitle className="text-destructive">Could not load form</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" asChild>
              <Link to="/f">Back to forms</Link>
            </Button>
          </CardContent>
        </Card>
      ) : form ? (
        <>
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-muted-foreground">
              <ClipboardList className="h-4 w-4" />
              <span className="text-xs font-medium uppercase tracking-wider">Form</span>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-3xl font-semibold tracking-tight">{form.title}</h1>
              <Badge
                variant="outline"
                className={cn('capitalize', statusBadgeClass(form.status))}
              >
                {form.status}
              </Badge>
            </div>
            <p className="font-mono text-sm text-muted-foreground">{form.slug}</p>
          </div>

          {form.description ? (
            <p className="text-muted-foreground">{form.description}</p>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-2">
            <Card className="border-border/70">
              <CardHeader className="pb-2">
                <CardDescription>Submissions</CardDescription>
                <CardTitle className="text-3xl tabular-nums">
                  {form.submissionCount ?? 0}
                </CardTitle>
              </CardHeader>
            </Card>
            <Card className="border-border/70">
              <CardHeader className="pb-2">
                <CardDescription>Published version</CardDescription>
                <CardTitle className="text-3xl tabular-nums">
                  {typeof form.publishedVersion === 'number' ? form.publishedVersion : '—'}
                </CardTitle>
              </CardHeader>
            </Card>
          </div>

          <Card className="border-border/60 bg-muted/15">
            <CardHeader>
              <CardTitle className="text-base">Activity</CardTitle>
              <CardDescription>
                {form.lastSubmissionAt
                  ? `Last submission ${formatDistanceToNow(new Date(form.lastSubmissionAt), { addSuffix: true })}`
                  : 'No submissions yet'}
              </CardDescription>
            </CardHeader>
          </Card>

          <Card className="border-border/70">
            <CardHeader className="flex flex-col gap-4 space-y-0 sm:flex-row sm:items-center sm:justify-between">
              <div className="space-y-1">
                <CardTitle className="text-base">Student submissions</CardTitle>
                <CardDescription>
                  Responses from the student portal and public form. Open the full workspace to filter, export, and review each
                  row — it loads separately so this overview stays quick.
                </CardDescription>
              </div>
              <Button className="w-full shrink-0 gap-2 sm:w-auto" asChild>
                <Link to={`/f/${form._id}/submissions`}>
                  View all submissions
                  <ChevronRight className="h-4 w-4" aria-hidden />
                </Link>
              </Button>
            </CardHeader>
          </Card>

          {appointmentFieldOptions.length > 0 ? (
            <Card className="border-border/70">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <CalendarCheck2 className="h-4 w-4 text-muted-foreground" />
                  <CardTitle className="text-base">Appointment attendance</CardTitle>
                </div>
                <CardDescription>
                  After students book, mark attended or not attended for each slot (only for fields with tracking enabled on a
                  published version).
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button variant="outline" size="sm" asChild>
                  <Link to={`/f/${form._id}/attendance`}>Open attendance list</Link>
                </Button>
              </CardContent>
            </Card>
          ) : null}

          {appointmentFieldOptions.length > 0 && !permLoading && canViewInstructorAssignments ? (
            <Card id="form-instructor-assignments" className="border-border/70 scroll-mt-6">
              <CardHeader>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <UserRound className="h-4 w-4 text-muted-foreground" />
                      <CardTitle className="text-base">Instructor assignments</CardTitle>
                    </div>
                    <CardDescription>
                      Assign SUB_ADMIN instructors to each appointment field for a date range. Existing bookings can be
                      backfilled so the instructor portal shows the right students. Date ranges use each field&apos;s schedule time
                      zone from the draft form definition (form builder).
                    </CardDescription>
                  </div>
                  <Button type="button" variant="secondary" size="sm" className="shrink-0 gap-2" onClick={() => void copyInstructorAttendanceUrl()}>
                    <Copy className="h-4 w-4" />
                    Copy instructor link
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <FormAppointmentInstructorsPanel
                  formId={form._id}
                  publishedVersion={form.publishedVersion}
                  appointmentFields={appointmentFieldOptions}
                  draftAppointmentFields={draftAppointmentFields}
                />
              </CardContent>
            </Card>
          ) : null}

          <Card className="border-border/70">
            <CardHeader>
              <CardTitle className="text-base">Reschedule fee waivers</CardTitle>
              <CardDescription>
                Grant or revoke one-time waivers when a student should not pay the appointment reschedule fee (e.g. school closure).
                Waivers apply to the draft form&apos;s appointment field ids below.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <RescheduleFeeWaiversPanel formId={form._id} appointmentFields={appointmentFieldOptions} />
            </CardContent>
          </Card>
        </>
      ) : null}
    </div>
  );
}
